// utils/membershipStatus.js

export function getMembershipStatus(user) {
    const um = user?.user_membership;
    if (!um) return { state: "none", programId: null, program: null, programName: null, cardNumber: null };

    const program = um.usercard?.loyalty_program || null;
    const deactivation = um.deactivation_date ? new Date(um.deactivation_date) : null;
    const isExpired = um.status !== "active" || (deactivation && deactivation < new Date());

    return {
        state: isExpired ? "expired" : "active",
        programId: program?.program_id ?? null,
        program,
        programName: program?.program_name || "Membership",
        cardNumber: um.usercard?.card_number || null, // <- adjust field name if different
        planAmount: program?.membership_type === "paid" ? Number(program.membership_amount) || 0 : 0,
        activationDate: um.activation_date,
        deactivationDate: um.deactivation_date,
        pointsBalance: um.points_balance,
    };
}

/**
 * program_ids the user actively (not expired) owns - i.e. free access, no fee.
 * An expired membership is deliberately excluded: it needs to be renewed (paid)
 * before it counts as "owned" again. Feed this into getVisibleRewards / a
 * MembershipPage's ownedProgramIds.
 */
export function getOwnedProgramIds(user) {
    const status = getMembershipStatus(user);
    return status.state === "active" && status.programId != null ? [status.programId] : [];
}

const DAY_MS = 24 * 60 * 60 * 1000;
const round2 = (n) => Math.round(n * 100) / 100;

/**
 * Unused value of the user's current paid plan, used as credit when upgrading.
 *
 *   perDay = planAmount / totalDays      (activation -> deactivation, 365 if the dates are missing)
 *   credit = perDay * daysLeft
 *
 * Only an ACTIVE paid plan earns credit; an expired or free plan returns 0.
 * NOTE: planAmount is the program's current price. If a customer can have paid a different amount
 * (old price, coupon), feed the amount they actually paid into the base instead - and compute the
 * same number on the server, which must be the source of truth for what is charged.
 */
export function getMembershipCredit(user, now = new Date()) {
    const none = { amount: 0, daysLeft: 0, totalDays: 0, perDay: 0 };
    const status = getMembershipStatus(user);
    if (status.state !== "active" || !(status.planAmount > 0) || !status.deactivationDate) return none;

    const end = new Date(status.deactivationDate).getTime();
    if (!Number.isFinite(end)) return none;
    const start = status.activationDate ? new Date(status.activationDate).getTime() : NaN;

    const spanDays = Number.isFinite(start) ? Math.round((end - start) / DAY_MS) : 0;
    const totalDays = spanDays > 0 ? spanDays : 365;
    const daysLeft = Math.min(totalDays, Math.max(0, Math.ceil((end - now.getTime()) / DAY_MS)));
    if (daysLeft <= 0) return none;

    const perDay = status.planAmount / totalDays;
    return { amount: round2(perDay * daysLeft), daysLeft, totalDays, perDay: round2(perDay) };
}

/** What an upgrade to `program` costs once the credit is taken off (never below 0). */
export function getUpgradeQuote(program, credit) {
    const price = program?.membership_type === "paid" ? Number(program.membership_amount) || 0 : 0;
    const applied = Math.min(Number(credit?.amount) || 0, price);
    return { price, credit: applied, net: round2(price - applied), daysLeft: credit?.daysLeft || 0 };
}

/**
 * Order-side membership object ({ membershipId, name, owned, action, fee, fullFee, credit, creditDays, ... })
 * used by every screen that starts a membership purchase, so the upgrade credit is applied in one place.
 * sel = { program, action: "use" | "join" | "buy" | "renew" | "upgrade" | "downgrade" }
 * credit = getMembershipCredit(user)
 */
export function buildOrderMembership(sel, credit) {
    if (!sel) return null;
    const full = ["buy", "renew", "upgrade"].includes(sel.action) ? Number(sel.program.membership_amount) || 0 : 0;
    const applied = sel.action === "upgrade" ? Math.min(Number(credit?.amount) || 0, full) : 0;
    return {
        membershipId: sel.program.program_id,
        name: sel.program.program_name,
        owned: sel.action === "use",
        action: sel.action,
        fee: round2(full - applied), // what is actually charged
        fullFee: full,
        credit: applied,
        creditDays: applied > 0 ? credit?.daysLeft || 0 : 0,
        maxCoinPercent: sel.program.max_coin_usage_percent ?? 100,
    };
}